import { getIdFromUrn }       from '@widesk-core/tools/identifier';
import { parseJsonDocument }  from '@tokamap/core';
import { withProjectThemes }  from '@tokamap/core';
import type { ShapeThemes }   from '@tokamap/core';
import compareMapsByPosition  from '@/TokamapEditor/helpers/compareMapsByPosition';

import type MapModel     from '@models/map/MapModel';
import type { PreviewMap } from '@/TokamapEditor/stores/PreviewMapsStore';

type PairableMap = Pick<MapModel, 'id' | 'label'> & Partial<Pick<MapModel, 'position'>>;

/** `mapId` absent : la source ne nomme pas ses cartes. */
type ProjectDocument = { mapId?: id; json: unknown };

/**
 * Met chaque carte du projet en regard du document reçu pour elle. `PreviewMap.mapId` est le
 * `documentId` que visent les zaaps : un appariement décalé les ferait pointer sur la carte du
 * voisin sans rien casser de visible.
 *
 * `GET /projects/{id}/json` nomme ses cartes (`{ urn, label, json }`) et l'appariement se fait par id.
 * Le fichier condensé d'une publication ne rend que des documents nus : il ne reste que leur
 * position, d'où le tri par id croissant et le refus d'apparier à moitié.
 */
export function pairMapsWithDocuments(
	maps: PairableMap[],
	documents: unknown[],
	themes: ShapeThemes = {},
): PreviewMap[] {
	const knownMaps = maps.filter(map => !!map.id);
	const entries = documents.map(toProjectDocument);

	if (entries.length && entries.every(entry => entry.mapId !== undefined)) {
		return toPreviewMaps(knownMaps.map(map => ({
			map,
			json: entries.find(entry => String(entry.mapId) === String(map.id))?.json,
		})), themes);
	}

	const sortedMaps = knownMaps.slice().sort((a, b) => Number(a.id) - Number(b.id));

	if (entries.length !== sortedMaps.length) {
		console.error(
			`Aperçu : ${entries.length} document(s) reçu(s) du projet pour ${sortedMaps.length} carte(s) connue(s).`,
		);

		return [];
	}

	return toPreviewMaps(sortedMaps.map((map, index) => ({ map, json: entries[index]?.json })), themes);
}

/**
 * Le document d'UNE carte, retrouvé par son id — `undefined` si la réponse n'en rend aucun pour elle.
 * Pas de repli par position, contrairement à l'appariement complet : servir la carte du voisin serait
 * pire que de ne rien servir.
 */
export function documentOfMap(documents: unknown[], mapId: id): unknown {
	const entry = documents
		.map(toProjectDocument)
		.find(document => String(document.mapId) === String(mapId));

	// L'enveloppe de la réponse projet est déjà sérialisée, le document peut l'être aussi.
	return typeof entry?.json === 'string' ? JSON.parse(entry.json) : entry?.json;
}

function toProjectDocument(entry: unknown): ProjectDocument {
	if (!entry || typeof entry !== 'object' || !('json' in entry)) return { json: entry };

	const { id, urn, json } = entry as { id?: id; urn?: Urn; json?: unknown };

	// La réponse désigne la carte par son urn (`$:map:map:2`) ; `map.id` est l'id nu.
	return { mapId: id ?? (urn ? getIdFromUrn(urn) : undefined), json };
}

function toPreviewMaps(paired: { map: PairableMap; json: unknown }[], themes: ShapeThemes): PreviewMap[] {
	return paired
		.slice()
		.sort((a, b) => compareMapsByPosition(a.map, b.map))
		.map(({ map, json }) => toPreviewMap(map, json, themes))
		.filter((map): map is PreviewMap => map !== null);
}

/** `null` quand le document est absent ou illisible : l'aperçu s'ouvre sur les autres cartes. */
function toPreviewMap(map: PairableMap, json: unknown, themes: ShapeThemes): PreviewMap | null {
	if (json === undefined) return null;

	try {
		// L'enveloppe de la réponse projet est déjà sérialisée, le document peut l'être aussi.
		const document = withProjectThemes(typeof json === 'string' ? JSON.parse(json) : json, themes);

		const parsed = parseJsonDocument(document);

		return {
			mapId: map.id,
			label: map.label,
			json: document,
			shapes: parsed.shapes,
			geolocation: parsed.view?.geolocation ?? false,
		};
	} catch (err) {
		console.error(`Aperçu : analyse du document de la carte "${map.label}"`, err);

		return null;
	}
}
